import type React from 'react';
import { useEffect, useRef } from 'react';

import {
  joinAuthorizedOrderRoom,
  leaveAuthorizedOrderRoom,
  onCartUpdate,
  onOrderCreated,
  onOrderUpdate,
  onProductUpdate
} from '../../../lib/socket';
import { normalizeCartLines } from '../../cart/utils/cartNormalization';
import type { Order } from '../types/orders.types';

type CartItem = { id: string; quantity: number };
type ProductLike = { id?: string; _id?: string } & Record<string, unknown>;

type UseOrderRealtimeArgs<TProduct> = {
  currentUserId?: string | null;
  orders: Order[];
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  setProducts: React.Dispatch<React.SetStateAction<TProduct[]>>;
  setLastSyncTime: React.Dispatch<React.SetStateAction<Date | null>>;
  onCartSnapshot?: (payload: { items?: CartItem[]; cartVersion?: number }) => void;
};

const getEntityId = (value: unknown): string => {
  if (!value || typeof value !== 'object') return '';
  const record = value as { id?: unknown; _id?: unknown; orderId?: unknown };
  return String(record.id || record._id || record.orderId || '').trim();
};

export function useOrderRealtime<TProduct>({
  currentUserId,
  orders,
  setOrders,
  setCart,
  setProducts,
  setLastSyncTime,
  onCartSnapshot
}: UseOrderRealtimeArgs<TProduct>) {
  /**
   * D3 (hard launch v4): stable order-room socket diff.
   *
   * Pre-v4 the join/leave effect listed `[orders]` as a dep, so any
   * change to the orders array (a single field on a single order
   * being patched by a realtime push) tore down every socket room
   * and rejoined it. That broke socket back-pressure, generated a
   * lot of `room:join` / `room:leave` traffic, and intermittently
   * dropped events that arrived during the gap.
   *
   * The fix: keep a `useRef` of the previously-joined room IDs and
   * only join/leave the *diff* between the previous set and the
   * current set. The orders array can re-shuffle freely without
   * causing churn, because we compare by orderId, not by reference.
   *
   * On `currentUserId` change (login / logout) we fully reset by
   * leaving every room first, then re-joining.
   */
  const joinedRoomsRef = useRef<Map<string, { orderId: string; customerId: string; driverId: string }>>(new Map());
  const previousUserIdRef = useRef<string | null | undefined>(currentUserId);

  useEffect(() => {
    if (previousUserIdRef.current !== currentUserId) {
      // Hard reset on auth change.
      for (const entry of joinedRoomsRef.current.values()) {
        leaveAuthorizedOrderRoom(entry);
      }
      joinedRoomsRef.current = new Map();
      previousUserIdRef.current = currentUserId;
    }
    if (!currentUserId) return;

    const desired = new Map<string, { orderId: string; customerId: string; driverId: string }>();
    for (const order of orders) {
      const orderId = String(order.id || order._id || order.legacyId || '').trim();
      if (!orderId) continue;
      desired.set(orderId, {
        orderId,
        customerId: String(order.customerId || '').trim(),
        driverId: String(order.driverId || '').trim()
      });
    }

    // Leave rooms we no longer want.
    for (const [orderId, entry] of joinedRoomsRef.current.entries()) {
      if (!desired.has(orderId)) {
        leaveAuthorizedOrderRoom(entry);
        joinedRoomsRef.current.delete(orderId);
      }
    }

    // Join rooms we don't already have.
    for (const [orderId, entry] of desired.entries()) {
      const existing = joinedRoomsRef.current.get(orderId);
      if (!existing) {
        joinAuthorizedOrderRoom(entry);
        joinedRoomsRef.current.set(orderId, entry);
        continue;
      }
      // If customerId/driverId changed (e.g. driver reassigned), refresh.
      if (existing.customerId !== entry.customerId || existing.driverId !== entry.driverId) {
        leaveAuthorizedOrderRoom(existing);
        joinAuthorizedOrderRoom(entry);
        joinedRoomsRef.current.set(orderId, entry);
      }
    }
  }, [currentUserId, orders]);

  // Final unmount: leave anything we still hold.
  useEffect(() => {
    return () => {
      for (const entry of joinedRoomsRef.current.values()) {
        leaveAuthorizedOrderRoom(entry);
      }
      joinedRoomsRef.current = new Map();
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return;

    const unsubCart = onCartUpdate(data => {
      const payload = data as { items?: CartItem[]; cartVersion?: number } | null;
      if (!payload) return;
      if (onCartSnapshot) {
        onCartSnapshot(payload);
      } else if (Array.isArray(payload.items)) {
        setCart(normalizeCartLines(payload.items));
      }
      setLastSyncTime(new Date());
    });

    const unsubOrderUpdate = onOrderUpdate(order => {
      const updatedOrder = order as Partial<Order> | null;
      const updatedId = getEntityId(updatedOrder);
      if (!updatedId) return;

      const normalizedUpdate: Partial<Order> = {
        ...updatedOrder,
        ...(typeof updatedOrder?.amountCapturedCents === 'number' && typeof updatedOrder?.capturedAmount !== 'number'
          ? { capturedAmount: updatedOrder.amountCapturedCents / 100 }
          : {}),
        ...(typeof updatedOrder?.amountAuthorizedCents === 'number' && typeof updatedOrder?.authorizedAmount !== 'number'
          ? { authorizedAmount: updatedOrder.amountAuthorizedCents / 100 }
          : {})
      };

      setOrders(prev => {
        const existingIndex = prev.findIndex(o => o.id === updatedId);
        if (existingIndex === -1) return prev;

        return prev.map(o => (o.id === updatedId ? { ...o, ...normalizedUpdate, id: updatedId } : o));
      });
      setLastSyncTime(new Date());
    });

    const unsubOrderCreate = onOrderCreated(order => {
      const createdOrder = order as Order;
      const createdId = getEntityId(createdOrder);
      if (!createdId) return;

      const normalizedCreated: Order = {
        ...createdOrder,
        id: createdId,
        ...(typeof createdOrder?.amountCapturedCents === 'number' && typeof createdOrder?.capturedAmount !== 'number'
          ? { capturedAmount: createdOrder.amountCapturedCents / 100 }
          : {}),
        ...(typeof createdOrder?.amountAuthorizedCents === 'number' && typeof createdOrder?.authorizedAmount !== 'number'
          ? { authorizedAmount: createdOrder.amountAuthorizedCents / 100 }
          : {})
      };

      setOrders(prev => {
        const existingIndex = prev.findIndex(existing => existing.id === createdId);
        if (existingIndex >= 0) {
          return prev.map(existing =>
            existing.id === createdId ? { ...existing, ...normalizedCreated, id: createdId } : existing
          );
        }

        return [normalizedCreated, ...prev];
      });
      setLastSyncTime(new Date());
    });

    const unsubProduct = onProductUpdate(raw => {
      const envelope = raw as { action?: string; sku?: string; payload?: ProductLike } | ProductLike | null;
      if (envelope && typeof envelope === 'object' && 'action' in envelope) {
        const action = String(envelope.action || '').toLowerCase();
        if (action === 'delete') {
          const deletedSku = String(envelope.sku || '').trim();
          if (!deletedSku) return;
          setProducts(prev =>
            prev.filter(p => {
              const pid = String((p as ProductLike).id || (p as ProductLike)._id || (p as { sku?: string }).sku || '').trim();
              return pid !== deletedSku;
            })
          );
          setLastSyncTime(new Date());
          return;
        }
        if (action === 'bulk-import') {
          // Catalog bulk import — next explicit product fetch refreshes; avoid partial merges.
          setLastSyncTime(new Date());
          return;
        }
      }
      const updatedProduct =
        envelope && typeof envelope === 'object' && 'payload' in envelope && envelope.payload
          ? envelope.payload
          : (envelope as ProductLike | null);
      if (!updatedProduct || typeof updatedProduct !== 'object') return;
      const updatedId =
        getEntityId(updatedProduct) || String((envelope as { sku?: string })?.sku || '').trim();
      if (!updatedId) return;
      const patch = updatedProduct as Record<string, unknown>;
      setProducts(prev =>
        prev.map(p => {
          const pid = String((p as ProductLike).id || (p as ProductLike)._id || (p as { sku?: string }).sku || '').trim();
          return pid === updatedId
            ? ({ ...(p as Record<string, unknown>), ...patch } as TProduct)
            : p;
        })
      );
      setLastSyncTime(new Date());
    });

    return () => {
      unsubCart();
      unsubOrderUpdate();
      unsubOrderCreate();
      unsubProduct();
    };
  }, [currentUserId, onCartSnapshot, setCart, setLastSyncTime, setOrders, setProducts]);
}
